import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN, REFRESH_TOKEN, USER_INFO } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';
import FirebaseService from 'services/FirebaseService';

// Get stored user to check mustChangePassword
const storedUser = JSON.parse(localStorage.getItem(USER_INFO) || 'null');

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	refreshToken: localStorage.getItem(REFRESH_TOKEN) || null,
	user: storedUser,
	// Check if stored user has mustChangePassword flag
	mustChangePassword: storedUser?.mustChangePassword || false,
}

// Sign in with username/email and password
export const signIn = createAsyncThunk('auth/signIn', async (data, { rejectWithValue }) => {
	try {
		const response = await AuthService.login(data);
		
		if (response.token || response.accessToken) {
			const accessToken = response.accessToken || response.token;
			const refreshToken = response.refreshToken;
			const user = response.user;
			const mustChangePassword = response.mustChangePassword || false;

			// Store tokens
			localStorage.setItem(AUTH_TOKEN, accessToken);
			if (refreshToken) {
				localStorage.setItem(REFRESH_TOKEN, refreshToken);
			}
			if (user) {
				localStorage.setItem(USER_INFO, JSON.stringify(user));
			}

			return {
				token: accessToken,
				refreshToken,
				user,
				mustChangePassword,
			};
		} else {
			return rejectWithValue(response.message || 'Login failed');
		}
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || err.message || 'Login failed');
	}
});

// Get current user info
export const fetchCurrentUser = createAsyncThunk('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.getCurrentUser();
		if (response.user) {
			localStorage.setItem(USER_INFO, JSON.stringify(response.user));
			return response.user;
		}
		return rejectWithValue('Failed to fetch user info');
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Failed to fetch user info');
	}
});

// Sign out
export const signOut = createAsyncThunk('auth/signOut', async (_, { getState }) => {
	try {
		const refreshToken = getState().auth.refreshToken;
		await AuthService.logout(refreshToken);
	} catch (err) {
		// Continue with logout even if API call fails
		console.error('Logout API error:', err);
	}
	
	// Clear all stored data
	localStorage.removeItem(AUTH_TOKEN);
	localStorage.removeItem(REFRESH_TOKEN);
	localStorage.removeItem(USER_INFO);
	
	return null;
});

// Sign out from all devices
export const signOutAll = createAsyncThunk('auth/signOutAll', async () => {
	try {
		await AuthService.logoutAll();
	} catch (err) {
		console.error('Logout all API error:', err);
	}
	
	localStorage.removeItem(AUTH_TOKEN);
	localStorage.removeItem(REFRESH_TOKEN);
	localStorage.removeItem(USER_INFO);
	
	return null;
});

// Sign up (Firebase-based, kept for backward compatibility)
export const signUp = createAsyncThunk('auth/signUp', async (data, { rejectWithValue }) => {
	const { email, password } = data;
	try {
		const response = await FirebaseService.signUpEmailRequest(email, password);
		if (response.user) {
			const token = response.user.refreshToken;
			localStorage.setItem(AUTH_TOKEN, token);
			return token;
		} else {
			return rejectWithValue(response.message?.replace('Firebase: ', ''));
		}
	} catch (err) {
		return rejectWithValue(err.message || 'Error');
	}
});

// Sign in with Google (Firebase-based)
export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
	try {
		const response = await FirebaseService.signInGoogleRequest();
		if (response.user) {
			const token = response.user.refreshToken;
			localStorage.setItem(AUTH_TOKEN, token);
			return token;
		} else {
			return rejectWithValue(response.message?.replace('Firebase: ', ''));
		}
	} catch (err) {
		return rejectWithValue(err.message || 'Error');
	}
});

// Sign in with Facebook (Firebase-based)
export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
	try {
		const response = await FirebaseService.signInFacebookRequest();
		if (response.user) {
			const token = response.user.refreshToken;
			localStorage.setItem(AUTH_TOKEN, token);
			return token;
		} else {
			return rejectWithValue(response.message?.replace('Firebase: ', ''));
		}
	} catch (err) {
		return rejectWithValue(err.message || 'Error');
	}
});

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false;
			state.redirect = '/';
			state.token = action.payload.token;
			state.refreshToken = action.payload.refreshToken;
			state.user = action.payload.user;
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload;
			state.showMessage = true;
			state.loading = false;
		},
		hideAuthMessage: (state) => {
			state.message = '';
			state.showMessage = false;
		},
		signOutSuccess: (state) => {
			state.loading = false;
			state.token = null;
			state.refreshToken = null;
			state.user = null;
			state.redirect = '/';
			localStorage.removeItem(AUTH_TOKEN);
			localStorage.removeItem(REFRESH_TOKEN);
			localStorage.removeItem(USER_INFO);
		},
		showLoading: (state) => {
			state.loading = true;
		},
		signInSuccess: (state, action) => {
			state.loading = false;
			state.token = action.payload.token;
			state.refreshToken = action.payload.refreshToken;
			state.user = action.payload.user;
		},
		updateTokens: (state, action) => {
			state.token = action.payload.token;
			if (action.payload.refreshToken) {
				state.refreshToken = action.payload.refreshToken;
			}
		},
		updateUser: (state, action) => {
			state.user = action.payload;
			localStorage.setItem(USER_INFO, JSON.stringify(action.payload));
		},
		clearMustChangePassword: (state) => {
			state.mustChangePassword = false;
		},
	},
	extraReducers: (builder) => {
		builder
			// Sign In
			.addCase(signIn.pending, (state) => {
				state.loading = true;
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = action.payload.mustChangePassword ? '' : '/';
				state.token = action.payload.token;
				state.refreshToken = action.payload.refreshToken;
				state.user = action.payload.user;
				state.mustChangePassword = action.payload.mustChangePassword || false;
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			// Sign Out
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false;
				state.token = null;
				state.refreshToken = null;
				state.user = null;
				state.redirect = '/';
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false;
				state.token = null;
				state.refreshToken = null;
				state.user = null;
				state.redirect = '/';
			})
			// Sign Out All
			.addCase(signOutAll.fulfilled, (state) => {
				state.loading = false;
				state.token = null;
				state.refreshToken = null;
				state.user = null;
				state.redirect = '/';
			})
			// Fetch Current User
			.addCase(fetchCurrentUser.fulfilled, (state, action) => {
				state.user = action.payload;
			})
			.addCase(fetchCurrentUser.rejected, (state) => {
				// If we can't fetch user, might need to re-auth
				console.error('Failed to fetch current user');
			})
			// Sign Up
			.addCase(signUp.pending, (state) => {
				state.loading = true;
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload;
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			// Sign In With Google
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true;
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload;
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			// Sign In With Facebook
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true;
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload;
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			});
	},
});

export const { 
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	updateTokens,
	updateUser,
	clearMustChangePassword,
} = authSlice.actions;

export default authSlice.reducer;
